import React, { useState, useEffect } from 'react';
import { Menu, X, Code, Smartphone, Globe, Zap, Users, TrendingUp, ChevronRight, ArrowRight, Check, Moon, Sun, ChevronDown } from 'lucide-react';
import { regionLinksData, servicesData } from './data';

export default function FlyCodeRebrand() {
	const [scrolled, setScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [activeService, setActiveService] = useState(0);
	const [darkMode, setDarkMode] = useState(true);
	const [expandedTab, setExpandedTab] = useState(0);
	const [expandedService, setExpandedService] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalType, setModalType] = useState('privacy');

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 50);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			setActiveService((prev) => (prev + 1) % 3);
		}, 4000);
		return () => clearInterval(interval);
	}, []);

	const services = [
		{
			icon: <Globe className="w-8 h-8" />,
			title: "Web Development",
			description: "Cutting-edge web applications built with React, Next.js, and modern frameworks that scale with your business.",
			color: "from-cyan-500 to-blue-600"
		},
		{
			icon: <Smartphone className="w-8 h-8" />,
			title: "Mobile Solutions",
			description: "Native and cross-platform mobile apps that deliver seamless experiences on iOS and Android.",
			color: "from-purple-500 to-pink-600"
		},
		{
			icon: <Code className="w-8 h-8" />,
			title: "Custom Software",
			description: "Tailored digital solutions designed to solve your unique business challenges and drive growth.",
			color: "from-orange-500 to-red-600"
		}
	];

	const stats = [
		{ number: "150+", label: "Projects Delivered" },
		{ number: "50+", label: "Happy Clients" },
		{ number: "2", label: "Countries Served" },
		{ number: "99%", label: "Client Satisfaction" }
	];

	const features = [
		{ icon: <Zap />, title: "Lightning Fast", desc: "Optimized performance for exceptional user experiences" },
		{ icon: <Users />, title: "Client-Focused", desc: "Your success is our success - we're partners in growth" },
		{ icon: <TrendingUp />, title: "Scalable Solutions", desc: "Built to grow with your business needs" }
	];

	const portfolioProjects = [
		{ 
			name: "South Africa Website", 
			url: "https://sa.flycodeinv.com/",
			flag: "🇿🇦",
			color: "from-cyan-500 to-blue-600"
		},
		{ 
			name: "Nigeria Website", 
			url: "https://nga.flycodeinv.com/",
			flag: "🇳🇬",
			color: "from-purple-500 to-pink-600"
		},
		{ 
			name: "Echo Rift", 
			url: "https://sa.flycodeinv.com/echorift",
			flag: "🎵",
			color: "from-pink-500 to-rose-600"
		},
		{ 
			name: "ForLet - Marketplace", 
			url: "https://forlet.com.ng",
			flag: "🏪",
			color: "from-blue-500 to-cyan-600"
		},
		{ 
			name: "MasterUwa", 
			url: "https://master-uwa.free.nf",
			flag: "📚",
			color: "from-indigo-500 to-purple-600"
		},
		{ 
			name: "eCommerce Demo", 
			url: "https://mariellashop.free.nf/?i=1",
			flag: "🛍️",
			color: "from-orange-500 to-pink-600"
		}
	];

	const aboutTabs = [
		{
			title: "Our Mission",
			content: "At Fly Code Innovation, we're committed to transforming businesses across Africa through cutting-edge digital solutions. We believe in creating technology that drives real business results and makes a lasting impact."
		},
		{
			title: "Our Team",
			content: "Our team consists of passionate developers, designers, and strategists with years of experience in building world-class digital products. We're dedicated to excellence and client success."
		},
		{
			title: "Why Us",
			content: "We combine technical expertise with business acumen. Our solutions are scalable, secure, and designed to grow with your business. We're not just service providers—we're your digital transformation partners."
		},
		{
			title: "Our Values",
			content: "Innovation, Integrity, and Impact are at the core of everything we do. We prioritize transparency, continuous learning, and delivering exceptional value to our clients."
		}
	];

	const modalContent = {
		privacy: {
			title: "Privacy Policy",
			content: "At Fly Code Innovation, we are committed to protecting your privacy. We collect and process personal data only for legitimate business purposes. Your information is securely stored and never shared with third parties without consent. We comply with all applicable data protection regulations including GDPR and CCPA. For questions about our privacy practices, contact us at privacy@flycodeinv.com."
		},
		terms: {
			title: "Terms of Service",
			content: "By using our services, you agree to these terms. Our services are provided on an 'as-is' basis. We reserve the right to modify our services and these terms at any time. Payment terms are net 30 days from invoice date. All work is protected by intellectual property laws. Clients retain ownership of their deliverables upon full payment. Disputes shall be resolved through binding arbitration."
		},
		sitemap: {
			title: "Sitemap",
			content: "Fly Code Innovation Website Sitemap:\n\n• Home\n• Services (Web Development, Mobile Solutions, E-Commerce, Cloud Solutions, UI/UX Design, Maintenance & Support)\n• Portfolio (South Africa Website, Nigeria Website, Echo Rift, ForLet, MasterUwa, eCommerce Demo)\n• About Us\n• Contact\n\nFor a detailed XML sitemap, visit: /sitemap.xml"
		}
	};

	return (
		<div className={`min-h-screen overflow-x-hidden transition-colors duration-300 ${darkMode ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'}`}>
			<style>{`
				@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;700;800&display=swap');
				
				* {
					margin: 0;
					padding: 0;
					box-sizing: border-box;
				}

				body {
					font-family: 'Space Mono', monospace;
				}

				.gradient-text {
					background: linear-gradient(135deg, #00f5ff 0%, #ff00ff 50%, #ffaa00 100%);
					-webkit-background-clip: text;
					-webkit-text-fill-color: transparent;
					background-clip: text;
				}

				.glow {
					text-shadow: 0 0 20px rgba(0, 245, 255, 0.5),
											 0 0 40px rgba(255, 0, 255, 0.3);
				}

				.card-hover {
					transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
				}

				.card-hover:hover {
					transform: translateY(-8px) scale(1.02);
				}

				@keyframes float {
					0%, 100% { transform: translateY(0px) rotate(0deg); }
					50% { transform: translateY(-20px) rotate(5deg); }
				}

				.floating {
					animation: float 6s ease-in-out infinite;
				}

				@keyframes slideIn {
					from {
						opacity: 0;
						transform: translateX(-50px);
					}
					to {
						opacity: 1;
						transform: translateX(0);
					}
				}

				.slide-in {
					animation: slideIn 0.8s ease-out forwards;
				}

				@keyframes pulse-glow {
					0%, 100% { opacity: 0.5; }
					50% { opacity: 1; }
				}

				.pulse-glow {
					animation: pulse-glow 3s ease-in-out infinite;
				}

				.noise {
					background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E");
				}

				.grid-pattern {
					background-image: 
						linear-gradient(rgba(0, 245, 255, 0.05) 1px, transparent 1px),
						linear-gradient(90deg, rgba(0, 245, 255, 0.05) 1px, transparent 1px);
					background-size: 50px 50px;
				}
			`}</style>

			{/* Navigation */}
			<nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? `${darkMode ? 'bg-slate-900/95' : 'bg-white/95'} backdrop-blur-lg shadow-xl border-b ${darkMode ? 'border-cyan-500/20' : 'border-cyan-200'}` : 'bg-transparent'}`}>
				<div className="max-w-7xl mx-auto px-6 py-4">
					<div className="flex justify-between items-center">
						<div className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Syne, sans-serif' }}>
							<img src="/full_logo.png" alt="FCI Logo" className="w-10 h-10" />
							<span className="gradient-text glow">FLY CODE</span>
						</div>
						
						{/* Desktop Menu */}
						<div className="hidden md:flex space-x-8 items-center">
							{['Services', 'Portfolio', 'About', 'Contact'].map((item) => (
								<a 
									key={item} 
									href={`#${item.toLowerCase()}`} 
									className={`transition-colors duration-300 relative group ${darkMode ? 'hover:text-cyan-400' : 'hover:text-cyan-600'}`}
								>
									{item}
									<span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-500 group-hover:w-full transition-all duration-300`}></span>
								</a>
							))}
							<button 
								onClick={() => setDarkMode(!darkMode)}
								className={`p-2 rounded-full transition-all ${darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-200 hover:bg-slate-300'}`}
							>
								{darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-700" />}
							</button>
							<a href="mailto:info@flycodeinv.com" className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 transform hover:scale-105">
								Get Started
							</a>
						</div>

						{/* Mobile Menu Button & Theme Toggle */}
						<div className="md:hidden flex items-center gap-4">
							<button 
								onClick={() => setDarkMode(!darkMode)}
								className={`p-2 rounded-full transition-all ${darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-200 hover:bg-slate-300'}`}
							>
								{darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-700" />}
							</button>
							<button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
								{mobileMenuOpen ? <X /> : <Menu />}
							</button>
						</div>
					</div>
				</div>

				{/* Mobile Menu */}
				{mobileMenuOpen && (
					<div className={`md:hidden ${darkMode ? 'bg-slate-900 border-cyan-500/20' : 'bg-slate-100 border-cyan-200'} border-t`}>
						<div className="px-6 py-4 space-y-4">
							{['Services', 'Portfolio', 'About', 'Contact'].map((item) => (
								<a 
									key={item} 
									href={`#${item.toLowerCase()}`} 
									className={`block transition-colors ${darkMode ? 'hover:text-cyan-400' : 'hover:text-cyan-600'}`}
									onClick={() => setMobileMenuOpen(false)}
								>
									{item}
								</a>
							))}
							<a href="mailto:info@flycodeinv.com" className="w-full block text-center px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
								Get Started
							</a>
						</div>
					</div>
				)}
			</nav>

			{/* Hero Section */}
			<section className={`relative min-h-screen flex items-center justify-center overflow-hidden noise grid-pattern ${darkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-slate-50 to-cyan-50'}`}>
				{/* Animated Background Elements */}
				<div className="absolute inset-0 overflow-hidden">
					<div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl floating"></div>
					<div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl floating" style={{ animationDelay: '2s' }}></div>
					<div className="absolute top-1/2 left-1/2 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl floating" style={{ animationDelay: '4s' }}></div>
				</div>

				<div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
					<div className="slide-in">
						<h1 className="text-6xl md:text-8xl font-black mb-6 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
							Building The
							<br />
							<span className="gradient-text glow">Digital Future</span>
							<br />
							of Africa
						</h1>
					</div>
					<p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-slate-300 slide-in" style={{ animationDelay: '0.2s' }}>
						Innovative web & mobile solutions that transform businesses across South Africa and Nigeria
					</p>
					<div className="flex flex-col sm:flex-row gap-6 justify-center slide-in" style={{ animationDelay: '0.4s' }}>
						<a href="mailto:info@flycodeinv.com" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2">
							Start Your Project <ArrowRight className="w-5 h-5" />
						</a>
						<a href="#services" className="px-8 py-4 border-2 border-cyan-400 rounded-full font-bold text-lg hover:bg-cyan-400/10 transition-all duration-300 flex items-center justify-center gap-2">
							View Our Work <ChevronRight className="w-5 h-5" />
						</a>
					</div>

					{/* Stats */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 slide-in" style={{ animationDelay: '0.6s' }}>
						{stats.map((stat, idx) => (
							<div key={idx} className="text-center">
								<div className="text-4xl md:text-5xl font-black gradient-text mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
									{stat.number}
								</div>
								<div className="text-slate-400 text-sm">{stat.label}</div>
							</div>
						))}
					</div>
				</div>

				{/* Scroll Indicator */}
				<div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 pulse-glow">
					<div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex justify-center">
						<div className="w-1 h-3 bg-cyan-400 rounded-full mt-2 animate-bounce"></div>
					</div>
				</div>
			</section>
		{/* Mobile Showcase Section */}
		<section className={`py-24 relative overflow-hidden ${darkMode ? 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-b from-white via-slate-50 to-white'}`}>
			<div className="max-w-7xl mx-auto px-6">
				<div className="grid md:grid-cols-2 gap-16 items-center">
					{/* Left Content */}
					<div>
						<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
							Responsive Design
							<br />
							<span className="gradient-text">For All Devices</span>
						</h2>
						<p className={`text-xl mb-8 leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
							Our websites and applications are built mobile-first, ensuring perfect performance whether accessed on smartphones, tablets, or desktops. Every pixel is optimized for the ultimate user experience.
						</p>
						<div className="space-y-4">
							{[
								{ icon: '📱', title: 'Mobile Optimized', desc: 'Perfect on every smartphone' },
								{ icon: '⚡', title: 'Lightning Fast', desc: 'Optimized performance and speed' },
								{ icon: '♿', title: 'Accessible', desc: 'WCAG compliant design' }
							].map((feature, idx) => (
								<div key={idx} className="flex gap-4 items-start">
									<span className="text-3xl">{feature.icon}</span>
									<div>
										<h4 className="font-bold text-lg">{feature.title}</h4>
										<p className={darkMode ? 'text-slate-400' : 'text-slate-600'}>{feature.desc}</p>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Right - Phone Mockup */}
					<div className="flex justify-center items-center">
						<div className="relative w-80 h-auto">
							{/* Phone Frame */}
							<div className={`rounded-3xl p-3 shadow-2xl ${darkMode ? 'bg-slate-900 border-2 border-slate-800' : 'bg-white border-2 border-slate-300'}`}>
								{/* Phone Notch */}
								<div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-950' : 'bg-slate-100'} relative`}>
									{/* Top Bar with Notch */}
									<div className={`h-8 flex items-center justify-between px-6 ${darkMode ? 'bg-slate-900' : 'bg-gradient-to-r from-cyan-400 to-purple-500'}`}>
										<span className="text-xs font-semibold">9:41</span>
										<div className={`h-6 w-24 rounded-full ${darkMode ? 'bg-slate-950' : 'bg-black'}`}></div>
										<span className="text-xs">📶</span>
									</div>

									{/* Screen Content */}
									<div className={`p-6 min-h-96 ${darkMode ? 'bg-gradient-to-b from-slate-900 to-slate-950' : 'bg-gradient-to-b from-cyan-50 to-blue-50'} flex flex-col justify-center`}>
										{/* Header */}
										<div className="mb-6">
											<h3 className="text-2xl font-black gradient-text mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
												FLY CODE
											</h3>
											<p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
												Digital Solutions
											</p>
										</div>

										{/* Content Cards */}
										<div className="space-y-3">
											{['Web Dev', 'Mobile Apps', 'Startups'].map((item, idx) => (
												<div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-white'} border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
													<p className="text-xs font-semibold">{item}</p>
													<p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Touch to explore</p>
												</div>
											))}
										</div>

										{/* CTA Button */}
										<button className="mt-6 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-xs font-bold text-white hover:shadow-lg transition-all">
											Get Started
										</button>
									</div>
								</div>

								{/* Phone Bezel Bottom */}
								<div className={`h-4 mt-3 rounded-b-2xl ${darkMode ? 'bg-slate-800' : 'bg-slate-300'}`}></div>
							</div>

							{/* Floating Badge */}
							<div className={`absolute -top-4 -right-4 px-4 py-2 rounded-full ${darkMode ? 'bg-gradient-to-r from-cyan-500 to-purple-600' : 'bg-gradient-to-r from-cyan-400 to-purple-500'} text-xs font-bold shadow-lg`}>
								Responsive ✓
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
			{/* Services Section */}
			<section id="services" className={`py-24 relative overflow-hidden ${darkMode ? 'bg-slate-950' : 'bg-white'}`}>
				<div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-b from-white via-slate-50 to-white'}`}></div>
				
				<div className="relative z-10 max-w-7xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
							Our <span className="gradient-text">Services</span>
						</h2>
						<p className={`text-xl ${darkMode ? 'text-slate-400' : 'text-slate-600'} max-w-2xl mx-auto`}>
							Comprehensive digital solutions tailored to your business needs
						</p>
					</div>

					<div className="max-w-4xl mx-auto space-y-4">
						{servicesData.map((service, idx) => (
							<div
								key={idx}
								className={`rounded-2xl border-2 transition-all duration-300 ${
									darkMode
										? `${expandedService === idx ? 'bg-slate-800 border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-slate-800/50 border-slate-700'}`
										: `${expandedService === idx ? 'bg-white border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-slate-50 border-slate-200'}`
								}`}
							>
								<button
									onClick={() => setExpandedService(expandedService === idx ? -1 : idx)}
									className={`w-full px-8 py-6 flex items-center justify-between transition-all ${
										expandedService === idx ? 'rounded-t-2xl' : 'rounded-2xl'
									}`}
								>
									<div className="flex items-center gap-4">
										<span className="text-4xl">{service.icon}</span>
										<h3 className="text-xl font-bold text-left" style={{ fontFamily: 'Syne, sans-serif' }}>
											{service.title}
										</h3>
									</div>
									<ChevronDown
										className={`w-6 h-6 transition-transform duration-300 flex-shrink-0 ${
											expandedService === idx ? 'transform rotate-180' : ''
										}`}
									/>
								</button>
								
								{expandedService === idx && (
									<div className={`px-8 pb-6 border-t-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
										<p className={`text-lg leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
											{service.description}
										</p>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Features Section */}
			<section id="about" className={`py-24 relative overflow-hidden ${darkMode ? 'bg-gradient-to-br from-cyan-950/20 via-slate-950 to-purple-950/20' : 'bg-gradient-to-br from-cyan-50/20 via-white to-purple-50/20'}`}>
				<div className="max-w-7xl mx-auto px-6">
					<div className="grid md:grid-cols-2 gap-16 items-center">
						<div>
							<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
								Why Choose <span className="gradient-text">Fly Code?</span>
							</h2>
							<p className="text-xl text-slate-400 mb-8">
								We don't just build applications - we craft digital experiences that drive real business results.
							</p>
							
							<div className="space-y-6">
								{features.map((feature, idx) => (
									<div key={idx} className="flex gap-4 items-start">
										<div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
											{feature.icon}
										</div>
										<div>
											<h3 className="text-xl font-bold mb-2">{feature.title}</h3>
											<p className={darkMode ? 'text-slate-400' : 'text-slate-600'}>{feature.desc}</p>
										</div>
									</div>
								))}
							</div>
						</div>

						<div className="relative">
							<div className={`rounded-2xl p-8 border transition-all ${
								darkMode 
									? 'bg-slate-900/50 backdrop-blur-sm border-cyan-500/20'
									: 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200'
							}`}>
								<h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
									Our Process
								</h3>
								<div className="space-y-4">
									{['Discovery & Strategy', 'Design & Prototype', 'Development', 'Testing & QA', 'Launch & Support'].map((step, idx) => (
										<div key={idx} className={`group relative flex items-center gap-4 p-5 rounded-xl transition-all transform hover:scale-105 cursor-pointer ${
											darkMode
												? 'bg-gradient-to-r from-slate-800/50 to-slate-700/50 hover:from-cyan-500/20 hover:to-purple-600/20 border border-transparent hover:border-cyan-400/50'
												: 'bg-gradient-to-r from-cyan-50/50 to-blue-50/50 hover:from-cyan-100 hover:to-blue-100 border border-cyan-200/50 hover:border-cyan-400'
										}`}>
											<div className={`w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full flex items-center justify-center font-bold text-sm shadow-lg transition-transform group-hover:scale-110`}>
												{idx + 1}
											</div>
											<span className="font-bold text-lg">{step}</span>
											<ChevronRight className="w-5 h-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Display Figures Section */}
			<section className={`py-24 relative overflow-hidden ${darkMode ? 'bg-slate-950' : 'bg-white'}`}>
				<div className="max-w-7xl mx-auto px-6">
					<div className="text-center mb-20">
						<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
							See Our Work
							<br />
							<span className="gradient-text">Across All Devices</span>
						</h2>
						<p className={`text-xl max-w-3xl mx-auto ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
							Beautiful, responsive design that looks stunning whether viewed on mobile, tablet, or desktop
						</p>
					</div>

					{/* Device Display Grid */}
					<div className="grid lg:grid-cols-3 gap-12 items-end">
						{/* Mobile Device */}
						<div className="flex justify-center">
							<div className="relative group device-phone transition-all duration-500">
								{/* Phone Body with realistic frame */}
								<div className="phone-frame w-64 rounded-3xl p-3">
									{/* Phone Camera Housing */}
									<div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10 opacity-60"></div>
									
									{/* Phone Display */}
									<div className="rounded-2xl overflow-hidden bg-black relative">
										{/* Status Bar */}
										<div className="h-7 flex items-center justify-between px-6 text-xs font-bold bg-black text-white">
											<span>9:41</span>
											<div className="w-20 h-5 rounded-full bg-black border border-gray-700"></div>
											<span>📶</span>
										</div>
										
										{/* Screen Content */}
										<div className={`h-96 p-4 flex flex-col screen-glass ${darkMode ? 'bg-gradient-to-b from-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
											<h3 className="text-xl font-black gradient-text mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>FLY CODE</h3>
											<div className="space-y-3 flex-1">
												<div className={`h-20 rounded-lg transition-all ${darkMode ? 'bg-gradient-to-r from-cyan-500/30 to-purple-600/30 border border-cyan-500/20' : 'bg-gradient-to-r from-cyan-100 to-purple-100 border border-cyan-300'}`}></div>
												<div className={`h-12 rounded-lg ${darkMode ? 'bg-slate-800/80' : 'bg-slate-200'}`}></div>
												<div className={`h-12 rounded-lg ${darkMode ? 'bg-slate-800/80' : 'bg-slate-200'}`}></div>
											</div>
											<button className="w-full py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-sm font-bold text-white shadow-lg hover:shadow-xl transition-all">Get Started</button>
										</div>
									</div>
									
									{/* Phone Bottom Bezel */}
									<div className="h-4 rounded-b-3xl mt-3 bg-gradient-to-b from-gray-700 to-black"></div>
									
									{/* Phone Side Rails */}
									<div className="absolute left-0 top-12 w-1 h-12 bg-gradient-to-b from-gray-600 to-gray-800 rounded"></div>
									<div className="absolute right-0 top-16 w-1 h-8 bg-gradient-to-b from-gray-600 to-gray-800 rounded"></div>
								</div>
								
								{/* Device Label */}
								<div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-xs font-bold text-white shadow-xl whitespace-nowrap">
									📱 iPhone 14
								</div>
							</div>
						</div>

						{/* Tablet Device */}
						<div className="flex justify-center lg:scale-110 pt-12">
							<div className="relative group device-tablet transition-all duration-500">
								{/* Tablet Body */}
								<div className="tablet-frame w-72 rounded-2xl p-4">
									{/* Tablet Camera */}
									<div className="absolute top-2 right-3 w-3 h-3 bg-black rounded-full z-10 shadow-lg"></div>
									
									{/* Display */}
									<div className="rounded-xl overflow-hidden bg-black">
										{/* Screen Content */}
										<div className={`h-80 p-6 flex flex-col screen-glass ${darkMode ? 'bg-gradient-to-b from-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
											<div className="flex items-center gap-3 mb-6">
												<img src="/full_logo.png" alt="Logo" className="w-8 h-8 drop-shadow-lg" />
												<h3 className="text-2xl font-black gradient-text" style={{ fontFamily: 'Syne, sans-serif' }}>FLY CODE</h3>
											</div>
											<div className="grid grid-cols-2 gap-4 flex-1">
												<div className={`rounded-lg p-4 transition-all ${darkMode ? 'bg-gradient-to-br from-cyan-500/30 to-purple-600/30 border border-cyan-500/20' : 'bg-gradient-to-br from-cyan-100 to-purple-100 border border-cyan-300'}`}>
													<p className="text-xs font-bold">Web Dev</p>
													<p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Responsive</p>
												</div>
												<div className={`rounded-lg p-4 transition-all ${darkMode ? 'bg-gradient-to-br from-cyan-500/30 to-purple-600/30 border border-cyan-500/20' : 'bg-gradient-to-br from-cyan-100 to-purple-100 border border-cyan-300'}`}>
													<p className="text-xs font-bold">Mobile</p>
													<p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Native</p>
												</div>
												<div className={`col-span-2 rounded-lg p-4 h-20 ${darkMode ? 'bg-slate-800/80 border border-slate-700/50' : 'bg-slate-200 border border-slate-300'}`}></div>
											</div>
											<button className="w-full py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-sm font-bold text-white shadow-lg hover:shadow-xl transition-all mt-4">Explore Services</button>
										</div>
									</div>
									
									{/* Tablet Bezels */}
									<div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-gray-800 to-gray-700 rounded-b-2xl"></div>
								</div>
								
								{/* Device Label */}
								<div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-xs font-bold text-white shadow-xl whitespace-nowrap">
									⌨️ iPad Pro
								</div>
							</div>
						</div>

						{/* Desktop Device */}
						<div className="flex justify-center pt-6">
							<div className="relative group device-desktop transition-all duration-500">
								{/* Monitor Housing */}
								<div className="desktop-frame w-96 rounded-3xl p-3">
									{/* Monitor Bezel Top */}
									<div className="bg-gradient-to-b from-gray-800 to-gray-700 rounded-t-2xl h-2"></div>
									
									{/* Screen */}
									<div className="rounded-b-2xl overflow-hidden bg-black">
										{/* Screen Content */}
										<div className={`h-56 p-8 flex flex-col screen-glass ${darkMode ? 'bg-gradient-to-b from-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
											<div className="flex items-center justify-between mb-6">
												<div className="flex items-center gap-3">
													<img src="/full_logo.png" alt="Logo" className="w-7 h-7 drop-shadow-lg" />
													<h3 className="text-2xl font-black gradient-text" style={{ fontFamily: 'Syne, sans-serif' }}>FLY CODE</h3>
												</div>
												<div className="flex gap-4 text-xs font-bold">
													<span className="text-cyan-400 hover:text-cyan-300 cursor-pointer">Services</span>
													<span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>|</span>
													<span className={`${darkMode ? 'text-slate-300' : 'text-slate-700'} hover:text-cyan-400 cursor-pointer`}>Portfolio</span>
												</div>
											</div>
											<div className="grid grid-cols-4 gap-3 flex-1">
												{['Web Development', 'Mobile Apps', 'UI/UX Design', 'Cloud Solutions'].map((item, idx) => (
													<div key={idx} className={`rounded-lg p-3 flex flex-col justify-center text-xs font-bold transition-all ${darkMode ? 'bg-gradient-to-br from-cyan-500/30 to-purple-600/30 border border-cyan-500/20 hover:border-cyan-500/50' : 'bg-gradient-to-br from-cyan-100 to-purple-100 border border-cyan-300 hover:border-cyan-400'}`}>
														{item.split(' ')[0]}
													</div>
												))}
											</div>
											<button className="w-full py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-xs font-bold text-white shadow-lg hover:shadow-xl transition-all mt-4">Start Your Project</button>
										</div>
									</div>
									
									{/* Monitor Stand */}
									<div className="flex justify-center items-end gap-8 px-12 py-4 bg-gradient-to-b from-gray-700 to-gray-800">
										<div className="w-16 h-12 rounded-b-lg bg-gradient-to-b from-gray-600 to-gray-900 shadow-inner border border-gray-700"></div>
										<div className="w-24 h-3 rounded-full bg-gradient-to-b from-gray-600 to-gray-900 shadow-lg border border-gray-700"></div>
										<div className="w-16 h-12 rounded-b-lg bg-gradient-to-b from-gray-600 to-gray-900 shadow-inner border border-gray-700"></div>
									</div>
									
									{/* Bezel Bottom Edge */}
									<div className="bg-gradient-to-b from-gray-700 to-black rounded-b-3xl h-3"></div>
								</div>
								
								{/* Device Label */}
								<div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-xs font-bold text-white shadow-xl whitespace-nowrap">
									🖥️ iMac 27"
								</div>
							</div>
						</div>
					</div>

					{/* Bottom Text */}
					<div className="text-center mt-16">
						<p className={`text-lg font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
							✨ Perfectly optimized for every screen size and experience level
						</p>
					</div>
				</div>
			</section>

			{/* About Section */}
			<section id="about" className={`py-24 relative overflow-hidden ${darkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
				<div className="max-w-7xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
							About <span className="gradient-text">Fly Code</span>
						</h2>
						<p className={`text-xl ${darkMode ? 'text-slate-400' : 'text-slate-600'} max-w-2xl mx-auto`}>
							Learn more about our vision, mission, and what drives us
						</p>
					</div>

					<div className="max-w-3xl mx-auto space-y-4">
						{aboutTabs.map((tab, idx) => (
							<div
								key={idx}
								className={`rounded-2xl border-2 transition-all duration-300 ${
									darkMode
										? `${expandedTab === idx ? 'bg-slate-800 border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-slate-800/50 border-slate-700'}`
										: `${expandedTab === idx ? 'bg-white border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-slate-50 border-slate-200'}`
								}`}
							>
								<button
									onClick={() => setExpandedTab(expandedTab === idx ? -1 : idx)}
									className={`w-full px-8 py-6 flex items-center justify-between transition-all ${
										expandedTab === idx ? 'rounded-t-2xl' : 'rounded-2xl'
									}`}
								>
									<h3 className="text-xl font-bold text-left" style={{ fontFamily: 'Syne, sans-serif' }}>
										{tab.title}
									</h3>
									<ChevronDown
										className={`w-6 h-6 transition-transform duration-300 ${
											expandedTab === idx ? 'transform rotate-180' : ''
										}`}
									/>
								</button>
								
								{expandedTab === idx && (
									<div className={`px-8 pb-6 border-t-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
										<p className={`text-lg leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
											{tab.content}
										</p>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Portfolio Section */}
			<section id="portfolio" className={`py-24 relative overflow-hidden ${darkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
				<div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50'}`}></div>
				
				<div className="relative z-10 max-w-7xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
							Our <span className="gradient-text">Portfolio</span>
						</h2>
						<p className={`text-xl ${darkMode ? 'text-slate-400' : 'text-slate-600'} max-w-2xl mx-auto`}>
							Showcasing our latest projects and digital solutions
						</p>
					</div>

					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{portfolioProjects.map((project, idx) => (
							<a
								key={idx}
								href={project.url}
								target={project.url.includes('http') ? '_blank' : '_self'}
								rel={project.url.includes('http') ? 'noopener noreferrer' : ''}
								className={`p-6 rounded-2xl border-2 card-hover transition-all group ${
									darkMode 
										? 'bg-slate-900/50 border-slate-800 hover:border-cyan-400' 
										: 'bg-white border-slate-200 hover:border-cyan-400'
								}`}
							>
								<div className={`flex items-center gap-4 mb-4`}>
									<span className="text-4xl">{project.flag}</span>
									<div className={`flex-1 h-12 bg-gradient-to-br ${project.color} rounded-lg`}></div>
								</div>
								<h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
									{project.name}
								</h3>
								<p className={`text-sm ${darkMode ? 'text-slate-400 group-hover:text-cyan-400' : 'text-slate-600 group-hover:text-cyan-600'} transition-colors`}>
									View Project {project.url.includes('http') && '↗'}
								</p>
							</a>
						))}
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section id="contact" className={`py-24 relative overflow-hidden ${darkMode ? 'bg-slate-950' : 'bg-white'}`}>
				<div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-r from-cyan-600 to-purple-600 opacity-10' : 'bg-gradient-to-r from-cyan-400 to-purple-500 opacity-5'}`}></div>
				<div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
					<h2 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
						Ready to <span className="gradient-text glow">Innovate?</span>
					</h2>
					<p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto">
						Let's transform your vision into reality. Get in touch and let's build something extraordinary together.
					</p>
					<a href="mailto:info@flycodeinv.com" className="inline-block px-12 py-5 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-bold text-xl hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 transform hover:scale-105">
						Start Your Project Today
					</a>
				</div>
			</section>

			{/* Footer */}
			<footer className={`relative overflow-hidden ${darkMode ? 'bg-gradient-to-b from-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-100 to-white'}`}>
				{/* Background decoration */}
				<div className={`absolute inset-0 ${darkMode ? 'opacity-20' : 'opacity-10'}`}>
					<div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
					<div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
				</div>

				<div className="relative z-10">
					{/* Top Section */}
					<div className="max-w-7xl mx-auto px-6 py-16 border-b" style={{borderBottomColor: darkMode ? '#334155' : '#e2e8f0'}}>
						<div className="grid md:grid-cols-4 gap-12">
							{/* Brand Section */}
							<div className="md:col-span-1">
								<div className="flex items-center gap-3 mb-4">
									<img src="/full_logo.png" alt="FCI Logo" className="w-12 h-12" />
									<div className="text-2xl font-black" style={{ fontFamily: 'Syne, sans-serif' }}>
										<span className="gradient-text">FLY</span>
									</div>
								</div>
								<p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
									Building digital solutions that transform businesses across Africa. Innovation, integrity, and impact.
								</p>
							</div>
							
							{/* Services Links */}
							<div>
								<h4 className="font-bold mb-6 text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>Services</h4>
								<ul className={`space-y-3 text-sm`}>
									<li><a href="#services" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}><span className="text-cyan-400">→</span> Web Development</a></li>
									<li><a href="#services" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}><span className="text-cyan-400">→</span> Mobile Apps</a></li>
									<li><a href="#services" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}><span className="text-cyan-400">→</span> Custom Software</a></li>
									<li><a href="mailto:info@flycodeinv.com" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}><span className="text-cyan-400">→</span> Contact Us</a></li>
								</ul>
							</div>

							{/* Company Links */}
							<div>
								<h4 className="font-bold mb-6 text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>Company</h4>
								<ul className={`space-y-3 text-sm`}>
									<li><a href="#about" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-purple-400' : 'text-slate-600 hover:text-purple-600'}`}><span className="text-purple-400">→</span> About Us</a></li>
									<li><a href="#portfolio" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-purple-400' : 'text-slate-600 hover:text-purple-600'}`}><span className="text-purple-400">→</span> Portfolio</a></li>
									<li><a href="#services" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-purple-400' : 'text-slate-600 hover:text-purple-600'}`}><span className="text-purple-400">→</span> Our Process</a></li>
									<li><a href="mailto:careers@flycodeinv.com" className={`inline-flex items-center gap-2 transition-all ${darkMode ? 'text-slate-400 hover:text-purple-400' : 'text-slate-600 hover:text-purple-600'}`}><span className="text-purple-400">→</span> Careers</a></li>
								</ul>
							</div>

							{/* Locations & CTA */}
							<div>
								<h4 className="font-bold mb-6 text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>Locations</h4>
								<div className={`space-y-3 text-sm mb-8 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
									<p className="flex items-center gap-2"><span className="text-xl">🇿🇦</span> South Africa</p>
									<p className="flex items-center gap-2"><span className="text-xl">🇳🇬</span> Nigeria</p>
								</div>
								<a href="mailto:info@flycodeinv.com" className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg font-semibold text-sm hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
									Get in Touch
								</a>
							</div>
						</div>
					</div>

					{/* Bottom Section */}
					<div className="max-w-7xl mx-auto px-6 py-8">
						<div className="flex flex-col md:flex-row justify-between items-center gap-4">
							<div className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-600'}`}>
							<p>&copy; 2020 - 2026 Fly Code Innovation. All Rights Reserved.</p>
						</div>
						<div className={`flex gap-6 text-sm`}>
							<button onClick={() => { setModalType('privacy'); setModalOpen(true); }} className={`transition-colors ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}>Privacy Policy</button>
							<button onClick={() => { setModalType('terms'); setModalOpen(true); }} className={`transition-colors ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}>Terms of Service</button>
							<button onClick={() => { setModalType('sitemap'); setModalOpen(true); }} className={`transition-colors ${darkMode ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}>Sitemap</button>
						</div>
					</div>
				</div>
			</div>
		</footer>

		{/* Modal */}
		{modalOpen && (
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
				{/* Backdrop */}
				<div 
					className={`absolute inset-0 ${darkMode ? 'bg-black/70' : 'bg-black/50'}`}
					onClick={() => setModalOpen(false)}
				></div>

				{/* Modal Content */}
				<div className={`relative z-50 max-w-2xl w-full rounded-2xl p-8 ${darkMode ? 'bg-slate-900' : 'bg-white'} shadow-2xl`}>
					<div className="flex justify-between items-center mb-6">
						<h2 className="text-3xl font-black" style={{ fontFamily: 'Syne, sans-serif' }}>
							<span className="gradient-text">{modalContent[modalType].title}</span>
						</h2>
						<button 
							onClick={() => setModalOpen(false)}
							className={`text-2xl font-bold transition-colors ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-black'}`}
						>
							×
						</button>
					</div>

					<div className={`mb-8 leading-relaxed whitespace-pre-line ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
						{modalContent[modalType].content}
					</div>

					<div className="flex justify-end gap-4">
						<button 
							onClick={() => setModalOpen(false)}
							className={`px-6 py-3 rounded-full font-semibold transition-all ${
								darkMode 
									? 'bg-slate-800 hover:bg-slate-700' 
									: 'bg-slate-200 hover:bg-slate-300'
							}`}
						>
							Close
						</button>
						<a 
							href="mailto:info@flycodeinv.com"
							className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all"
						>
							Contact Us
						</a>
					</div>
				</div>
			</div>
		)}
		</div>
	);
}
